import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import type { GameV1 } from '@/api/games';
import { createItems } from '@/helpers/common';
import { getGamesPlugs } from '@/helpers/game';
import { useTopGamePreview } from '@/hooks/useTopGamePreview';
import { GameCardFactory } from '@/modules/game/components';
import { useGetCommonStore } from '@/store';

import { ScGamesList } from './styled';

interface AddonsProps {
  isDemo: boolean;
  isFullScreen: boolean;
}

export const Addons: FC<AddonsProps> = (props) => {
  const { t } = useTranslation('common');
  const useCommonStore = useGetCommonStore();

  const { recentGames, isAuthenticated, favoriteGames } = useCommonStore(
    useShallow((state) => ({
      recentGames: state.recentGames,
      isAuthenticated: state.isAuthenticated,
      favoriteGames: state.favoriteGames,
    }))
  );

  const topGames = useTopGamePreview();

  const recentGamesList = useMemo(
    () => (recentGames.length ? recentGames : topGames),
    [recentGames, topGames]
  );

  return (
    <>
      {isAuthenticated && (
        <GameSection
          games={favoriteGames}
          title={t('game.favorites')}
          {...props}
        />
      )}
      <GameSection
        games={recentGamesList}
        title={t('game.recentlyPlayed')}
        {...props}
      />
    </>
  );
};

interface GameSectionProps extends AddonsProps {
  games: GameV1[];
  title: string;
}

const ROW_LENGTH = 4;

const GameSection: FC<GameSectionProps> = ({ games, title, isFullScreen }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('common');

  const [showAll, setShowAll] = useState(false);

  const gamesToShow = useMemo(
    () =>
      showAll
        ? games
        : games.slice(
            0,
            games.length <= ROW_LENGTH ? ROW_LENGTH : ROW_LENGTH - 1
          ),
    [games, showAll]
  );

  const plugs = useMemo(
    () =>
      games.length === 0
        ? createItems({}, ROW_LENGTH)
        : games.length <= ROW_LENGTH
          ? getGamesPlugs(gamesToShow, ROW_LENGTH, 1)
          : [],
    [games.length, gamesToShow]
  );

  return (
    <ScGamesList isFullScreen={isFullScreen}>
      <h4 className="title">{title}</h4>
      <ul className="list">
        {gamesToShow.map((rGame) => (
          <GameCardFactory game={rGame} isSmall key={rGame.id} withoutBottom />
        ))}
        {plugs.map((_, i) => (
          <GameCardFactory isEmpty isSmall key={i} withoutBottom />
        ))}
        {games.length > ROW_LENGTH && (
          <li
            className={`show-more__btn ${language.toLowerCase()}`}
            onClick={() => setShowAll((s) => !s)}
          >
            <div className="text">
              {showAll ? `${t('showLess')}` : `${t('showMore')}`}
            </div>
          </li>
        )}
      </ul>
    </ScGamesList>
  );
};
